import { CLASSIC_COLORS as C } from './invoiceEngine';
import { FitLine, FitZone } from './fitText';

/**
 * The classic Bangladeshi pad — the one every mofussil press prints: cream
 * stock, an orange-and-green banner across the top, the shop's name in red,
 * a green address band, red নাম / ঠিকানা tags over grey writing bars, a green
 * column head, an open writing area with a faint thank-you across it, and a
 * lime signature strip at the foot.
 *
 * Everything here is measured off a printed pad in A4 pixels and multiplied by
 * `u` (the paper's width against A4), so an A6 book is the same pad printed
 * small. The colours are the design itself, not a theme — only the green of
 * the band and the column head follows the chosen ink.
 */

/**
 * The banner's height, as a share of the paper's width — the block the press
 * prints, so it is fixed. Whatever the shop fills in is set to fit inside it
 * rather than pushing the pad's furniture down the sheet. It is kept under the
 * printed pad's own share, because the writing area is what a memo is for.
 */
const ART_RATIO = 70 / 271;


/** The words this pad prints that the other styles word differently. */
const CLASSIC_LABELS = {
    bn: { memoNo: 'ক্রমিক নং', inWords: 'কথায়', colon: 'ঃ' },
    en: { memoNo: 'SL No.', inWords: 'In words', colon: ':' },
};

/**
 * The banner artwork: an orange sweep, the cream wave the shop's name is set
 * on, and the green washing in from both bottom corners. Shapes only — every
 * word on the pad is HTML, so the export keeps the Bangla face instead of
 * falling back inside an image.
 */
function HeaderArt({ width, height }) {
    return (
        <svg
            width={width}
            height={height}
            viewBox="0 0 800 240"
            preserveAspectRatio="none"
            aria-hidden="true"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }}
        >
            <defs>
                <linearGradient id="cmOrange" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0" stopColor="#EF4A23" />
                    <stop offset="0.35" stopColor="#F5791F" />
                    <stop offset="0.7" stopColor="#FBA919" />
                    <stop offset="1" stopColor="#FDC70A" />
                </linearGradient>
                <radialGradient id="cmGreenL" cx="0.08" cy="1" r="1">
                    <stop offset="0" stopColor="#3E8F23" />
                    <stop offset="0.35" stopColor="#7FBB38" />
                    <stop offset="0.7" stopColor="#C4DA6E" />
                    <stop offset="1" stopColor="#D9E489" stopOpacity="0" />
                </radialGradient>
                <radialGradient id="cmGreenR" cx="0.92" cy="1" r="1">
                    <stop offset="0" stopColor="#3E8F23" />
                    <stop offset="0.35" stopColor="#7FBB38" />
                    <stop offset="0.7" stopColor="#C4DA6E" />
                    <stop offset="1" stopColor="#D9E489" stopOpacity="0" />
                </radialGradient>
            </defs>

            <rect x="0" y="0" width="800" height="240" fill="url(#cmOrange)" />

            {/* The two pale sweeps every press block carries. */}
            <path d="M0 96 C 170 24, 330 146, 520 72 C 650 22, 730 62, 800 39 L800 0 L0 0 Z" fill="#FFFFFF" opacity="0.2" />
            <path d="M0 54 C 150 118, 300 13, 470 74 C 620 130, 710 52, 800 85 L800 0 L0 0 Z" fill="#FFFFFF" opacity="0.14" />

            {/* The cream wave, then the green washing in from the two bottom
                corners — the middle stays cream, which the name is printed on. */}
            <path d="M0 126 C 120 82, 250 162, 430 128 C 590 100, 700 146, 800 110 L800 240 L0 240 Z" fill={C.paper} />
            <path d="M0 126 C 120 82, 250 162, 430 128 C 590 100, 700 146, 800 110" fill="none" stroke="#FFFFFF" strokeWidth="3" opacity="0.55" />
            <rect x="0" y="96" width="230" height="144" fill="url(#cmGreenL)" opacity="0.85" />
            <rect x="570" y="96" width="230" height="144" fill="url(#cmGreenR)" opacity="0.85" />
        </svg>
    );
}

/** The white disc with a handset in it, beside the shop's numbers. */
function PhoneMark({ size }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ display: 'block', flex: 'none' }}>
            <circle cx="12" cy="12" r="12" fill="#FFFFFF" />
            <path
                d="M8.2 5.4c.5 0 .8.3 1 .7l.9 2c.2.4.1.9-.2 1.2l-.8.8a8.6 8.6 0 0 0 4 4l.8-.8c.3-.3.8-.4 1.2-.2l2 .9c.4.2.7.5.7 1v2c0 .7-.6 1.3-1.3 1.2A12.4 12.4 0 0 1 5.5 6.7c0-.7.5-1.3 1.2-1.3Z"
                fill={C.ink}
            />
        </svg>
    );
}

/** A red tag with a tapered tip, and the grey bar the shopkeeper writes on. */
function TagBar({ label, u }) {
    const height = 40 * u;
    return (
        <div style={{ display: 'flex', alignItems: 'stretch', height }}>
            <div
                style={{
                    flex: 'none', background: C.red, color: '#FFFFFF',
                    display: 'flex', alignItems: 'center', padding: `0 ${10 * u}px`,
                    fontSize: 21 * u, fontWeight: 700, lineHeight: 1.2, whiteSpace: 'nowrap',
                }}
            >
                <span>{label}</span>
            </div>
            {/* The tip, as a border triangle: full height at the bar, nothing at
                its point, which is how the block is cut on the printed pad. */}
            <div
                style={{
                    flex: 'none', width: 0, height: 0,
                    borderTop: `${height}px solid ${C.red}`,
                    borderRight: `${15 * u}px solid transparent`,
                }}
            />
            <div style={{ flex: 1, background: C.field, border: `1px solid ${C.fieldLine}`, borderLeft: 'none' }} />
        </div>
    );
}

/** The ruled writing area: a green head, column splits, and an open body. */
function ClassicGrid({ memo, green, u }) {
    const totalFlex = memo.columns.reduce((sum, col) => sum + (col.flex || 1), 0);
    const rule = `1px solid ${C.rule}`;
    const last = memo.columns.length - 1;

    return (
        // No `height: 100%` here: a percentage against a flex-grown parent does
        // not resolve, and the table is stretched by the flex row instead.
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
                {memo.columns.map((col) => (
                    <col key={col.id} style={{ width: `${((col.flex || 1) / totalFlex) * 100}%` }} />
                ))}
            </colgroup>
            <thead>
                <tr>
                    {memo.columns.map((col, i) => (
                        <th
                            key={col.id}
                            style={{
                                background: green,
                                color: '#FFFFFF',
                                height: 50 * u,
                                padding: `0 ${3 * u}px`,
                                fontSize: 22 * u,
                                fontWeight: 700,
                                lineHeight: 1.3,
                                textAlign: 'center',
                                // The head is split by hairlines of the paper
                                // itself, not by ink, so the band reads solid.
                                borderLeft: memo.columnLines && i > 0 ? `1px solid ${C.paper}` : 'none',
                            }}
                        >
                            <span>{col.label || ' '}</span>
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {Array.from({ length: memo.rows }, (_, i) => (
                    <tr key={i}>
                        {memo.columns.map((col, c) => (
                            <td
                                key={col.id}
                                style={{
                                    height: 30 * u,
                                    padding: 0,
                                    borderLeft: (memo.columnLines || c === 0) ? rule : 'none',
                                    borderRight: (memo.columnLines || c === last) ? rule : 'none',
                                    borderBottom: memo.rowLines && i < memo.rows - 1 ? rule : 'none',
                                }}
                            />
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

/**
 * মোট / অগ্রিম / বাকী, ruled into the last two columns of the grid rather than
 * hung off the side — the same table widths, so the lines meet.
 */
function ClassicTotals({ memo, labels, u }) {
    const rows = [
        memo.totals.total ? labels.total : null,
        memo.totals.advance ? labels.advance : null,
        memo.totals.due ? labels.due : null,
    ].filter(Boolean);

    const count = memo.columns.length;
    const totalFlex = memo.columns.reduce((sum, col) => sum + (col.flex || 1), 0);
    const rule = `1px solid ${C.rule}`;
    const span = Math.max(0, count - 2);

    if (!rows.length) {
        // Nothing to total, but the writing area still has to be closed off.
        return <div style={{ borderTop: rule, borderLeft: rule, borderRight: rule, height: 0 }} />;
    }

    return (
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
                {memo.columns.map((col) => (
                    <col key={col.id} style={{ width: `${((col.flex || 1) / totalFlex) * 100}%` }} />
                ))}
            </colgroup>
            <tbody>
                {rows.map((label, i) => (
                    <tr key={label}>
                        {/* The columns left of the totals keep their own splits
                            all the way down, as the ruled pad does. */}
                        {memo.columns.slice(0, span).map((col, c) => (
                            <td
                                key={col.id}
                                style={{
                                    borderLeft: (memo.columnLines || c === 0) ? rule : 'none',
                                    borderRight: memo.columnLines && c === span - 1 ? rule : 'none',
                                    borderBottom: i === rows.length - 1 ? rule : 'none',
                                    height: 38 * u,
                                    padding: 0,
                                }}
                            />
                        ))}
                        <td
                            style={{
                                border: rule,
                                borderBottom: i === rows.length - 1 ? rule : 'none',
                                borderLeft: span > 0 && !memo.columnLines ? 'none' : rule,
                                height: 38 * u,
                                padding: `0 ${8 * u}px`,
                                fontSize: 22 * u,
                                fontWeight: 700,
                                lineHeight: 1.3,
                                textAlign: 'right',
                                color: C.ink,
                            }}
                        >
                            <span>{`${label}=`}</span>
                        </td>
                        <td
                            style={{
                                border: rule,
                                borderBottom: i === rows.length - 1 ? rule : 'none',
                                height: 38 * u,
                                padding: 0,
                            }}
                        />
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

/* ------------------------------------------------------------------ *
 *  The sheet
 * ------------------------------------------------------------------ */

export default function ClassicMemo({ memo, labels, title, accent, paperWidth, u }) {
    const { shop, show } = memo;
    const lang = memo.docLang === 'en' ? 'en' : 'bn';
    const words = CLASSIC_LABELS[lang];
    const green = accent || C.green;
    const pad = 53 * u;
    // With the artwork off the banner keeps only the room its lines need.
    // A switch the saved pad has never heard of counts as on — the same reading
    // the form's chips take, so a pad stored before a line existed still prints
    // it instead of the chip saying "on" while the sheet leaves it off.
    const on = (key) => show[key] !== false;
    const artHeight = Math.round(paperWidth * ART_RATIO);
    // Where the wave crosses the artwork: the contacts ride the orange above
    // it, the shop's name the cream below.
    const topHeight = Math.round(artHeight * 0.46);
    const footHeight = artHeight - topHeight;
    const topPad = { top: 12 * u, side: 28 * u, bottom: 2 * u };
    const footPad = { top: 0, side: 28 * u, bottom: 7 * u };
    const phones = [shop.phone, shop.phone2].filter((n) => String(n || '').trim());
    const email = String(shop.email || '').trim();
    // The address is typed as a block; the band prints it as one line.
    const addressLine = shop.address.split('\n').map((l) => l.trim()).filter(Boolean).join(', ');
    // What the banner has to fit: when any of it changes, it is measured again.
    const bannerKey = [
        memo.bismillah, title, memo.proprietor, memo.distributor, shop.name, shop.tagline,
        shop.logo ? '1' : '0', phones.join('|'), email, JSON.stringify(show), u,
    ].join('~');

    return (
        <>
            {/* ---- banner: a fixed block, whatever is written in it ---- */}
            <div style={{ position: 'relative', height: artHeight, overflow: 'hidden', flex: 'none' }}>
                {on('art') ? <HeaderArt width={paperWidth} height={artHeight} /> : null}

                {/* The top half: the invocation, the plate, and how to reach the shop. */}
                <FitZone height={topHeight} align="top" pad={topPad} signature={bannerKey}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 * u }}>
                        {/* The logo rides the corner of the sweep: beside the
                            plate it costs the banner no height at all. */}
                        <div style={{ flex: '1 1 0', minWidth: 0 }}>
                            {/* Drawn as a background in a box of its own size:
                                html2canvas measures the export's copy before the
                                picture has decoded, and an <img> sized by its own
                                proportions comes out stretched. */}
                            {on('logo') && shop.logo ? (
                                <div
                                    aria-hidden="true"
                                    style={{
                                        width: 130 * u, height: 54 * u,
                                        backgroundImage: `url("${shop.logo}")`,
                                        backgroundRepeat: 'no-repeat',
                                        backgroundPosition: 'left center',
                                        backgroundSize: 'contain',
                                    }}
                                />
                            ) : null}
                        </div>

                        <div style={{ flex: 'none', textAlign: 'center' }}>
                            {on('bismillah') && memo.bismillah.trim() ? (
                                <div style={{ fontSize: 13 * u, fontWeight: 600, lineHeight: 1.3, color: '#5A3A12' }}>
                                    {memo.bismillah}
                                </div>
                            ) : null}
                            {on('plate') ? (
                                <div
                                    style={{
                                        display: 'inline-block', marginTop: 6 * u,
                                        background: '#FFFFFF', color: C.ink,
                                        border: `1px solid ${C.ink}`, borderRadius: 999 * u,
                                        padding: `${3 * u}px ${18 * u}px`,
                                        fontSize: 19 * u, fontWeight: 700, lineHeight: 1.35,
                                    }}
                                >
                                    <span>{title}</span>
                                </div>
                            ) : null}
                        </div>

                        <div style={{ flex: '1 1 0', minWidth: 0, textAlign: 'right' }}>
                            {on('proprietor') && memo.proprietor.trim() ? (
                                <FitLine
                                    text={memo.proprietor}
                                    size={26 * u}
                                    style={{
                                        fontWeight: 800, lineHeight: 1.25, color: '#FFFFFF',
                                        textShadow: `0 ${1.5 * u}px ${2 * u}px rgba(90,40,0,0.45)`,
                                    }}
                                />
                            ) : null}
                            {/* Both numbers on one line: two stacked lines plus
                                an email crowd the corner of the block. */}
                            {on('phone') && phones.length ? (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 7 * u, marginTop: 3 * u }}>
                                    <PhoneMark size={24 * u} />
                                    <div style={{ flex: '0 1 auto', minWidth: 0 }}>
                                        <FitLine
                                            text={phones.join('  •  ')}
                                            size={18 * u}
                                            style={{
                                                fontWeight: 700, lineHeight: 1.35, color: '#FFFFFF',
                                                textShadow: `0 ${1 * u}px ${2 * u}px rgba(90,40,0,0.4)`,
                                            }}
                                        />
                                    </div>
                                </div>
                            ) : null}
                            {/* The email under the numbers, set in the same white
                                as the rest of the contact block. */}
                            {on('email') && email ? (
                                <FitLine
                                    text={email}
                                    size={15 * u}
                                    style={{
                                        fontWeight: 700, lineHeight: 1.35, color: '#FFFFFF', marginTop: 1 * u,
                                        textShadow: `0 ${1 * u}px ${2 * u}px rgba(90,40,0,0.5)`,
                                    }}
                                />
                            ) : null}
                        </div>
                    </div>
                </FitZone>

                {/* The bottom half: the shop's name on the cream of the wave. */}
                <FitZone height={footHeight} align="bottom" pad={footPad} signature={bannerKey}>
                    <div style={{ textAlign: 'center' }}>
                        <FitLine
                            text={shop.name || ' '}
                            size={58 * u}
                            style={{
                                fontWeight: 800, lineHeight: 1.12, color: C.red,
                                textShadow: `${-1.1 * u}px ${-1.1 * u}px 0 #FFFFFF, ${1.1 * u}px ${-1.1 * u}px 0 #FFFFFF, ${-1.1 * u}px ${1.1 * u}px 0 #FFFFFF, ${1.1 * u}px ${1.1 * u}px 0 #FFFFFF, ${2.5 * u}px ${3 * u}px ${2.5 * u}px rgba(90,40,0,0.38)`,
                            }}
                        />
                        {on('distributor') && memo.distributor.trim() ? (
                            <FitLine
                                text={memo.distributor}
                                size={25 * u}
                                style={{ fontWeight: 800, lineHeight: 1.3, color: '#23210F', marginTop: 2 * u }}
                            />
                        ) : null}
                        {on('tagline') && shop.tagline.trim() ? (
                            <div
                                style={{
                                    fontSize: 14.5 * u, fontWeight: 600, lineHeight: 1.38, color: C.soft,
                                    margin: `${1 * u}px auto 0`, maxWidth: '94%',
                                }}
                            >
                                {shop.tagline}
                            </div>
                        ) : null}
                    </div>
                </FitZone>
            </div>

            {/* ---- the green address band, edge to edge ---- */}
            {on('addressBand') && addressLine ? (
                <div
                    style={{
                        flex: 'none', background: green, color: '#FFFFFF', textAlign: 'center',
                        padding: `${5 * u}px ${28 * u}px`, fontSize: 23 * u, fontWeight: 700, lineHeight: 1.35,
                    }}
                >
                    <FitLine text={addressLine} size={23 * u} style={{ fontWeight: 700, lineHeight: 1.35 }} />
                </div>
            ) : null}

            {/* ---- the form ---- */}
            <div
                style={{
                    flex: 1, display: 'flex', flexDirection: 'column',
                    padding: `${12 * u}px ${pad}px ${26 * u}px`, boxSizing: 'border-box',
                }}
            >
                {(memo.fields.memoNo || memo.fields.date) ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 * u, marginBottom: 9 * u }}>
                        <span style={{ fontSize: 21 * u, fontWeight: 700, lineHeight: 1.4, color: C.ink }}>
                            {memo.fields.memoNo ? `${words.memoNo}-` : ''}
                        </span>
                        {memo.fields.date ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 4 * u }}>
                                {/* One string, not a label and a colon side by
                                    side: html2canvas shapes each text node on
                                    its own and the lone visarga is lost. */}
                                <span style={{ fontSize: 21 * u, fontWeight: 700, lineHeight: 1.4, color: C.ink, marginRight: 2 * u }}>
                                    {`${labels.date}${words.colon}`}
                                </span>
                                {[44, 44, 76].map((width, i) => (
                                    <span
                                        key={i}
                                        style={{
                                            display: 'block', width: width * u, height: 32 * u,
                                            background: '#FFFFFF', border: `1px solid ${C.rule}`,
                                        }}
                                    />
                                ))}
                            </span>
                        ) : null}
                    </div>
                ) : null}

                {/* No space before the visarga: on its own it has no base to
                    sit on and the Bangla faces print it with a dotted circle. */}
                {memo.fields.name ? <TagBar label={`${labels.name}${words.colon}`} u={u} /> : null}
                {memo.fields.address ? (
                    <div style={{ marginTop: 6 * u }}>
                        <TagBar label={`${labels.address}${words.colon}`} u={u} />
                    </div>
                ) : null}

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', marginTop: 8 * u }}>
                    <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'stretch' }}>
                        <ClassicGrid memo={memo} green={green} u={u} />
                    </div>
                    <ClassicTotals memo={memo} labels={labels} u={u} />

                    {/* The thank-you written faintly across the writing area. */}
                    {on('slogan') && memo.slogan.trim() ? (
                        <div
                            aria-hidden="true"
                            style={{
                                position: 'absolute', left: '8%', right: '22%', top: '52%',
                                textAlign: 'center', pointerEvents: 'none',
                                fontSize: 34 * u, fontWeight: 700, lineHeight: 1.2,
                                color: '#9A9178', opacity: 0.75, whiteSpace: 'nowrap',
                                transform: 'rotate(-4deg)',
                            }}
                        >
                            {memo.slogan}
                        </div>
                    ) : null}
                </div>

                {memo.showWords ? (
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 5 * u, marginTop: 14 * u }}>
                        <span style={{ fontSize: 20 * u, fontWeight: 700, lineHeight: 1.4, whiteSpace: 'nowrap', color: C.ink }}>
                            {`${words.inWords}${words.colon}`}
                        </span>
                        {/* On screen this is the pad's dotted leader; html2canvas
                            flattens it to a fine rule in the file, which is what
                            the press prints anyway. */}
                        <span style={{ flex: 1, borderBottom: `${2 * u}px dotted ${C.ink}`, height: 14 * u }} />
                    </div>
                ) : null}

                {memo.footerNote ? (
                    <div style={{ marginTop: 8 * u, fontSize: 16 * u, fontWeight: 600, lineHeight: 1.4, color: C.soft }}>
                        {memo.footerNote}
                    </div>
                ) : null}

                {/* The captions are the foot of the pad; the space over them is
                    what the two signatures are written in. */}
                {memo.showSignature ? (
                    <div
                        style={{
                            marginTop: 58 * u, background: C.lime, color: C.limeInk,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: `${4 * u}px ${12 * u}px`, fontSize: 18 * u, fontWeight: 700, lineHeight: 1.35,
                        }}
                    >
                        <span>{labels.customerSign}</span>
                        <span>{labels.sellerSign}</span>
                    </div>
                ) : null}
            </div>
        </>
    );
}
